import { IEssayQuestion, RaisedButton, UploadProgressArgs, useSnackbar } from '@eduact/ed-components'
// Vendored: keys options by id instead of text (see Components/VendoredExam/VENDORED.md).
import { TestQuestion } from '@Components/VendoredExam'
import { ANSWER_ATTACHMENT_ACCEPT, parseAttachmentAnswer, serializeAttachmentAnswer } from '@Components/VendoredExam/attachmentAnswer'
import Spacer from '@eduact/ed-components/Spacer'
import { FlexLayout } from '@eduact/ed-system'
import { uploadFileWithProgress } from '@Features/Base/BaseApi/BaseApi'
import useSubmitTest from '@Features/Test/Hooks/useSubmitTest'
import useSaveAnswer from '@Features/Test/Hooks/useSaveAnswer'
import { FormQuestion, useTest } from '@Features/Test/Providers/TestProvider'
import { useAppSelector } from '@Hooks/ReduxHooks'
import { SnackbarConfig } from '@Utilities/SnackbarConfig'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { PulseLoader } from 'react-spinners'
import styled from 'styled-components'

const MultipleViewMode = () => {
	const { formQuestions, setFormQuestions, formQuestionsRef, registerAnswerControl } = useTest()
	const { t } = useTranslation(['test'])
	const [searchParams, setSearchParams] = useSearchParams()
	const { isOnline } = useAppSelector(state => state.settings)
	const saveAnswer = useSaveAnswer()

	/**
	 * Apply an answer to state (by id) and start its save. The save promise is
	 * created synchronously so the final submit can flush the editors and then
	 * wait for exactly these saves before deciding what is still unsent.
	 */
	const handleOnChange = (answer: any, question: FormQuestion) => {
		setFormQuestions(draft => {
			const target = draft.find(_ => _.id === question.id)
			if (!target) return
			target.answer = answer
			target.submitted = false
		})
		if (answer !== null && isOnline) return saveAnswer(question, answer)
		return undefined
	}

	const handleFocus = (index: number, question: FormQuestion) => {
		setFormQuestions(draft => {
			draft[index].touched = true
		})
		searchParams.set('q', formQuestions[index].id.toString())
		setSearchParams(searchParams)
	}

	const submitTest = useSubmitTest()
	const [submitLoading, setSubmitLoading] = useState(false)

	const handleSubmitTest = async () => {
		setSubmitLoading(true)
		try {
			// submitTest flushes every registered editor and awaits the saves
			// those flushes started before it decides what is still unsent.
			await submitTest()
		} finally {
			setSubmitLoading(false)
		}
	}

	const { openSnackbar } = useSnackbar()
	const [uploadProgress, setUploadProgress] = useState<UploadProgressArgs | undefined>()
	const onAttachmentsChange = async (files: FileList | null, question: FormQuestion) => {
		if (!files || files.length === 0) return
		const formData = new FormData()
		for (let index = 0; index < files.length; index++) {
			const file = files.item(index)
			if (!file) return
			if (file.size / 1024 / 1024 > 5) {
				openSnackbar({ ...SnackbarConfig.disclaimer, text: t('file_too_large', { name: file.name }) })
				return
			}
			formData.set('file', file as Blob)
			formData.set('folder', 'answers')
			try {
				const {
					data: {
						data: { uri },
					},
				} = await uploadFileWithProgress({
					formData,
					progressCallback(progress) {
						setUploadProgress({
							file,
							progress,
						})
					},
				})
				setUploadProgress(undefined)
				// Read the CURRENT answer from the ref (a second file in the same
				// selection must see the first one); parse defensively, and only
				// merge into an attachment answer.
				const previous = (formQuestionsRef.current.find(_ => _.id === question.id) as IEssayQuestion | undefined)?.answer
				const urls = previous && previous.type === 'attachment' ? parseAttachmentAnswer(previous.answer) : []
				handleOnChange({ type: 'attachment', answer: serializeAttachmentAnswer([...urls, uri]) }, question)
			} catch (error) {
				setUploadProgress(undefined)
				openSnackbar({ ...SnackbarConfig.disclaimer, text: t('file_upload_failed') })
			}
		}
	}
	return (
		<TestWrapper online={isOnline}>
			{formQuestions.map((question, index) => {
				return (
					<div
						onMouseDown={() => {
							if (question.type === 'ordering') handleFocus(index, question)
						}}
						id={`question-${question.id}`}
						tabIndex={question.id}
						key={`${question.id}-${question.weight}`}
					>
						<TestQuestion
							essayQuestionProps={{
								onAttachmentsChange(files: FileList | null) {
									onAttachmentsChange(files, question)
								},
								// One registered editor per question id.
								onAnswerControl: (control: { flush(): unknown; cancel(): void } | null) => registerAnswerControl(question.id, control),
								uploadProgress,
								accept: ANSWER_ATTACHMENT_ACCEPT,
								textChangeDelay: 1000,
							}}
							index={index}
							question={question}
							onChange={answer => handleOnChange(answer, question)}
							key={question.id}
						/>
						<Spacer mb={'2rem'} />
					</div>
				)
			})}
			<FlexLayout mt="2rem" alignItems={'center'} justifyContent="flex-end">
				<RaisedButton disabled={!isOnline} btnSize={'medium'} outlined bgFallback onClick={handleSubmitTest}>
					{submitLoading ? <PulseLoader size={5} /> : <>{t('submit')}</>}
				</RaisedButton>
			</FlexLayout>
		</TestWrapper>
	)
}

export default MultipleViewMode

const TestWrapper = styled.div<{ online: boolean }>`
	pointer-events: ${props => !props.online && 'none'};
	opacity: ${props => !props.online && '0.5'};
`
